import * as T from 'three';
import type { FurnitureShape } from '@london-life/contracts';
import { createSceneKit, type SceneKit } from '@london-life/game/scene/kit';
import { WALL_HEIGHT, buildShell, dress, drawThing, dark, light } from '@london-life/game/house/room';
import type { Interior, VenueProp } from '@london-life/contracts';
import { drawPiece, isSetPiece } from './pieces';

// Draws a venue from its description, in the same block style as the city and the home: the shell, every
// prop (furniture shapes come from the home's builder, the rest are drawn here) and the place's sign.

const FURNITURE_SHAPES = new Set<string>([
  'bed',
  'wardrobe',
  'shower',
  'toilet',
  'sink',
  'cooker',
  'fridge',
  'table',
  'chair',
  'desk',
  'sofa',
  'armchair',
  'shelf',
  'tv',
  'rug',
  'plant',
  'lamp',
  'poster',
]);

function buildProp(k: SceneKit, prop: VenueProp, group: T.Group) {
  const { w, d, h, colour: c } = prop;
  if (isSetPiece(prop.shape)) {
    drawPiece(k, prop.shape, { w, d, h, c, variant: prop.variant, label: prop.label }, group);
    return;
  }
  if (FURNITURE_SHAPES.has(prop.shape)) {
    drawThing(k, { id: prop.shape, shape: prop.shape as FurnitureShape, size: { w, d, h }, colour: c }, group);
    return;
  }
  switch (prop.shape) {
    case 'counter': {
      k.box(w, h - 0.06, d, c, 0, (h - 0.06) / 2, 0, group);
      k.box(w + 0.08, 0.06, d + 0.08, light(c, 0.55), 0, h - 0.03, 0, group);
      break;
    }
    case 'stool': {
      k.cylinder(w / 2, 0.06, c, 0, h - 0.03, 0, group);
      k.cylinder(0.04, h - 0.06, dark(c, 0.7), 0, (h - 0.06) / 2, 0, group);
      break;
    }
    case 'stage': {
      k.box(w, h, d, c, 0, h / 2, 0, group);
      k.box(w * 0.98, 0.02, d * 0.98, light(c, 0.15), 0, h + 0.01, 0, group);
      k.cylinder(0.02, 1.3, '#2a2d31', w * 0.15, h + 0.65, d * 0.15, group); // a mic stand
      k.sphere(0.05, '#2a2d31', w * 0.15, h + 1.32, d * 0.15, group);
      break;
    }
    case 'tree': {
      k.cylinder(w * 0.1, h * 0.45, '#7a5534', 0, h * 0.225, 0, group, w * 0.12);
      k.sphere(w * 0.5, c, 0, h * 0.62, 0, group);
      k.sphere(w * 0.38, dark(c, 0.85), w * 0.22, h * 0.8, -w * 0.1, group);
      k.sphere(w * 0.32, light(c, 0.12), -w * 0.2, h * 0.86, w * 0.12, group);
      break;
    }
    case 'bench': {
      k.box(w, 0.06, d * 0.8, c, 0, h * 0.9, 0, group);
      k.box(w, h * 0.7, 0.05, dark(c), 0, h * 1.25, -d / 2 + 0.03, group); // back
      for (const side of [-1, 1])
        k.box(0.06, h * 0.9, d * 0.7, dark(c, 0.6), (side * (w - 0.12)) / 2, h * 0.45, 0, group);
      break;
    }
    case 'stall': {
      k.box(w, 0.9, d * 0.7, '#e7dcc7', 0, 0.45, d * 0.1, group); // the table
      k.box(w * 0.9, 0.15, d * 0.5, light(c, 0.5), 0, 0.97, d * 0.1, group); // goods on it
      for (const side of [-1, 1]) k.box(0.05, h, 0.05, '#3f3f46', (side * (w - 0.05)) / 2, h / 2, -d / 2 + 0.03, group);
      k.box(w + 0.2, 0.06, d + 0.2, c, 0, h, 0, group); // the awning
      k.box(w + 0.2, 0.25, 0.04, light(c, 0.35), 0, h - 0.14, d / 2 + 0.1, group);
      break;
    }
    case 'treadmill': {
      k.box(w, 0.18, d, c, 0, 0.09, 0, group);
      k.box(w * 0.7, 0.02, d * 0.85, '#111418', 0, 0.19, d * 0.04, group);
      for (const side of [-1, 1]) k.box(0.05, h, 0.05, '#7d8790', (side * (w - 0.05)) / 2, h / 2, -d / 2 + 0.05, group);
      k.box(w, 0.25, 0.2, '#2b3238', 0, h, -d / 2 + 0.1, group); // console
      break;
    }
    case 'weights': {
      for (const side of [-1, 1]) k.box(0.06, h, d, '#3c4349', (side * (w - 0.06)) / 2, h / 2, 0, group);
      for (const level of [0.35, 0.75, 1.15])
        if (level < h) {
          k.box(w, 0.04, 0.04, '#9aa3a8', 0, level, 0, group);
          for (const side of [-1, 1]) k.cylinder(0.14, 0.06, c, side * (w / 2 - 0.2), level, 0, group);
        }
      break;
    }
    case 'gate': {
      k.box(w, h, d, c, 0, h / 2, 0, group);
      k.box(0.6, 0.5, 0.03, '#bfe3ee', w / 2 + 0.3, h * 0.6, 0, group); // the glass flap
      k.box(w * 0.8, 0.02, d * 0.3, '#facc15', 0, h + 0.01, 0, group); // the card reader
      break;
    }
    case 'display': {
      k.box(w, h * 0.7, d, c, 0, (h * 0.7) / 2, 0, group);
      k.box(w * 0.9, h * 0.3, d * 0.9, '#cfe8f2', 0, h * 0.85, 0, group); // the glass case
      k.sphere(Math.min(w, d) * 0.18, '#b45309', 0, h * 0.82, 0, group); // something to look at
      break;
    }
    case 'screen': {
      k.box(w, h, d, '#20262c', 0, h / 2 + 0.6, 0, group);
      k.box(w * 0.92, h * 0.85, 0.01, c === '#0f172a' ? '#1e3a8a' : '#2f6fa3', 0, h / 2 + 0.6, d / 2 + 0.006, group);
      break;
    }
    case 'machine': {
      k.box(w, h, d, c, 0, h / 2, 0, group);
      k.box(w * 0.7, h * 0.35, 0.02, '#cfe8f2', 0, h * 0.65, d / 2 + 0.011, group);
      break;
    }
    case 'path': {
      k.box(w, 0.02, d, c, 0, 0.025, 0, group);
      break;
    }
    case 'thing': {
      k.box(w, h, d, c, 0, h / 2, 0, group);
      break;
    }
    case 'crates': {
      k.box(w, h * 0.55, d, c, 0, (h * 0.55) / 2, 0, group);
      k.box(w * 0.6, h * 0.45, d * 0.6, dark(c, 0.85), -w * 0.15, h * 0.55 + (h * 0.45) / 2, 0, group);
      break;
    }
  }
}

// a club's coloured lights, sweeping round the room
const PARTY = ['#ff2e88', '#00d4ff', '#7b2ff7'];

export function buildVenue(venue: Interior, options: { rich?: boolean; models?: boolean; night?: boolean } = {}) {
  const kit = createSceneKit();
  const group = new T.Group();
  const { w, d } = venue.floor;
  buildShell(kit, group, {
    w,
    d,
    wallColour: venue.wallColour,
    floorColour: venue.floorColour,
    outdoor: venue.outdoor,
    window: venue.window,
    ground: venue.ground,
    rich: options.rich,
    lights: venue.lights,
    floorStyle: venue.floorStyle,
    night: options.night,
  });
  const party: T.PointLight[] = [];
  if (venue.lights === 'party' && options.rich !== false)
    for (const colour of PARTY) {
      const lamp = new T.PointLight(colour, 9, Math.max(w, d) * 0.9, 1.5);
      lamp.position.y = 2.2;
      party.push(lamp);
      group.add(lamp);
    }
  // each prop's object, in the same order as the props (the Place Designer picks and moves them)
  const pieces: T.Group[] = [];
  for (const prop of venue.props) {
    const piece = new T.Group();
    pieces.push(piece);
    const blocks = new T.Group();
    buildProp(kit, prop, blocks);
    piece.add(blocks);
    if (options.models !== false)
      dress(kit, piece, blocks, {
        shape: prop.shape,
        model: prop.model,
        size: { w: prop.w, d: prop.d, h: prop.h },
        colour: prop.colour,
        x: prop.x,
        z: prop.z,
      });
    piece.position.set(prop.x, 0, prop.z);
    piece.rotation.y = (-(prop.turn ?? 0) * Math.PI) / 2;
    group.add(piece);
  }
  // the sign: on the back wall, or on a board outside
  const sign = venue.sign;
  // an outdoor place with an entrance arch carries its name there
  const arched = venue.props.some((prop) => prop.shape === 'arch' && prop.label);
  if (sign?.text && !(venue.outdoor && arched)) {
    const width = Math.min(3.2, Math.max(1.8, sign.text.length * 0.16));
    if (venue.outdoor) {
      kit.box(0.08, 1.6, 0.08, '#4b5563', -w / 2 + 0.6, 0.8, -d / 2 + 0.2, group);
      kit.sign(
        sign.text,
        -w / 2 + 0.6 + width / 2 - 0.1,
        1.45,
        -d / 2 + 0.25,
        group,
        width,
        sign.colour,
        sign.background,
      );
    } else
      kit.sign(
        sign.text,
        Math.min(w * 0.16, w / 2 - width / 2 - 0.4),
        WALL_HEIGHT - 0.4,
        -d / 2 + 0.03,
        group,
        width,
        sign.colour,
        sign.background,
      );
  }
  return {
    group,
    pieces,
    floor: { w, d },
    // the party lights move (seconds since the place opened)
    tick(t: number) {
      party.forEach((lamp, i) => {
        const a = t * 0.6 + (i * Math.PI * 2) / party.length;
        lamp.position.x = Math.cos(a) * w * 0.3;
        lamp.position.z = Math.sin(a) * d * 0.3;
      });
    },
    dispose() {
      kit.dispose();
    },
  };
}
export type VenueScene = ReturnType<typeof buildVenue>;
